// Full-screen lifecycle host for `/gatherings/:id/live`.
//
// Owns the three states a gathering moves through, so the URL stays the
// same as the user goes from "waiting for it to start" to "in the call"
// to "looking at the recap":
//
//   • scheduled — countdown + device check + gathering info; the host's
//                 "Start Gathering" button lives here.
//   • live      — mounts GatheringRoom (LiveKit). Transitions in
//                 automatically when the host starts; non-hosts get pulled
//                 in by the status poll.
//   • ended     — renders PostGathering inline so users land on the recap
//                 without a redirect.
//
// Polls the gathering every 5 s while not in the live room. Inside the
// live room, GatheringRoom owns its own LiveKit connection lifecycle.

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage, Badge, Button, Card, CardContent, CardHeader, useGql } from '@ah/design';
import { Calendar, Clock, Loader2, MapPin, Play, Users, Video } from 'lucide-react';
import { DeviceCheck } from '@ah/livekit/desktop';
import {
  GATHERING_QUERY,
  START_GATHERING_MUTATION,
  type GatheringDetail,
  type GatheringParticipantRow,
  type GatheringUser,
} from '../shared/index.ts';
import { GatheringRoom } from './GatheringRoom.tsx';
import { PostGathering } from './PostGathering.tsx';

// ── helpers ──────────────────────────────────────────────────────────

function userInitials(u: GatheringUser): string {
  if (u.firstName) return `${u.firstName[0]}${u.lastName?.[0] ?? ''}`.toUpperCase();
  return (u.name?.[0] ?? 'U').toUpperCase();
}
function displayName(u: GatheringUser): string {
  if (u.firstName) return `${u.firstName} ${u.lastName ?? ''}`.trim();
  return u.name ?? 'Unknown';
}
function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit',
  });
}
function useCountdown(targetIso: string): { days: number; hours: number; mins: number; secs: number; done: boolean } {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  const ms = Math.max(0, new Date(targetIso).getTime() - now);
  const total = Math.floor(ms / 1000);
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    mins: Math.floor((total % 3600) / 60),
    secs: total % 60,
    done: ms === 0,
  };
}

// ── scheduled state ──────────────────────────────────────────────────

function ScheduledView({ gathering, meId, onStarted }: {
  gathering: GatheringDetail;
  meId: string | null;
  onStarted: () => void;
}) {
  const gql = useGql();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const cd = useCountdown(gathering.startsAt);
  const isHost = !!meId && gathering.host?.id === meId;
  const id = gathering.id;

  const startMutation = useMutation({
    mutationFn: async () => {
      await gql.request(START_GATHERING_MUTATION, { id });
    },
    onSuccess: onStarted,
  });

  return (
    <div className="min-h-dvh bg-background">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <header className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-foreground truncate">{gathering.title}</h1>
            <p className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
              <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {formatDateTime(gathering.startsAt)}</span>
              {gathering.kind === 'offline' && gathering.location && (
                <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {gathering.location}</span>
              )}
              {gathering.kind === 'roundtable' && (
                <span className="flex items-center gap-1.5"><Video className="w-3.5 h-3.5" /> Online</span>
              )}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(`/gatherings/${id}`)}>Back to gathering</Button>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {cd.done ? 'Starting now' : 'Starts in'}
                </p>
              </CardHeader>
              <CardContent>
                {cd.done ? (
                  <div className="text-center py-4">
                    <p className="text-lg font-semibold text-foreground">Waiting for the host to start</p>
                    <p className="text-sm text-muted-foreground mt-1">You'll be pulled into the room automatically.</p>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    {cd.days > 0 && <CountdownUnit value={cd.days} label="days" />}
                    {(cd.days > 0 || cd.hours > 0) && (
                      <>
                        <span className="text-3xl font-bold text-muted-foreground -mt-3">:</span>
                        <CountdownUnit value={cd.hours} label="hrs" />
                      </>
                    )}
                    <span className="text-3xl font-bold text-muted-foreground -mt-3">:</span>
                    <CountdownUnit value={cd.mins} label="min" />
                    <span className="text-3xl font-bold text-muted-foreground -mt-3">:</span>
                    <CountdownUnit value={cd.secs} label="sec" />
                  </div>
                )}
                {isHost && (
                  <div className="mt-6 flex justify-center">
                    <Button onClick={() => { setStarting(true); startMutation.mutate(); }} disabled={starting || startMutation.isPending} size="lg">
                      {(starting || startMutation.isPending) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                      Start Gathering
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {gathering.kind === 'roundtable' && (
              <Card>
                <CardHeader>
                  <h2 className="font-semibold text-foreground">Set up your camera and microphone</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Pick your devices now — we'll remember them when the gathering starts.
                  </p>
                </CardHeader>
                <CardContent>
                  <DeviceCheck
                    joinLabel="I'm ready"
                    onContinue={(settings) => {
                      if (settings.cameraId) sessionStorage.setItem(`lk_camera_${id}`, settings.cameraId);
                      if (settings.micId) sessionStorage.setItem(`lk_mic_${id}`, settings.micId);
                    }}
                    onBack={() => navigate(`/gatherings/${id}`)}
                  />
                </CardContent>
              </Card>
            )}

            {gathering.description && (
              <Card>
                <CardHeader><h2 className="font-semibold text-foreground">About</h2></CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{gathering.description}</p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-4">
            <ParticipantsPanel participants={gathering.participants} />
          </div>
        </div>
      </div>
    </div>
  );
}

function CountdownUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-4xl font-bold tabular-nums text-foreground">{String(value).padStart(2, '0')}</span>
      <span className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{label}</span>
    </div>
  );
}

function ParticipantsPanel({ participants }: { participants: GatheringParticipantRow[] | null | undefined }) {
  const list = participants ?? [];
  return (
    <Card className="h-fit">
      <CardHeader>
        <h2 className="font-semibold text-foreground flex items-center gap-2">
          <Users className="w-4 h-4" /> Participants ({list.length})
        </h2>
      </CardHeader>
      <CardContent className="space-y-1">
        {list.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 -mx-2">
            <Avatar className="w-9 h-9 shrink-0">
              <AvatarImage src={p.user.pictureUrl ?? ''} alt={displayName(p.user)} />
              <AvatarFallback className="text-xs">{userInitials(p.user)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{displayName(p.user)}</p>
              <p className="text-xs text-muted-foreground capitalize">{p.role}</p>
            </div>
            {p.joinedAt && <Badge variant="secondary" className="text-[10px] shrink-0">Joined</Badge>}
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No participants yet</p>}
      </CardContent>
    </Card>
  );
}

// ── orchestrator ─────────────────────────────────────────────────────

export function GatheringLive() {
  const { id = '' } = useParams();
  const gql = useGql();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['gathering-live', id],
    queryFn: async () => gql.request<{ me: { id: string } | null; gathering: GatheringDetail | null }>(GATHERING_QUERY, { id }),
    enabled: !!id,
    // Poll for status transitions (scheduled → live → ended). Skipped while
    // the live room is mounted — GatheringRoom owns its own session there.
    refetchInterval: (q) => (q.state.data?.gathering?.status === 'live' ? false : 5000),
    refetchOnMount: 'always',
  });

  const gathering = data?.gathering;
  const meId = data?.me?.id ?? null;

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading gathering…</p>
        </div>
      </div>
    );
  }

  if (error || !gathering) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 max-w-md px-6">
          <p className="text-sm text-destructive">{(error as Error)?.message ?? 'Gathering not found.'}</p>
          <Button onClick={() => navigate('/gatherings')}>Back to gatherings</Button>
        </div>
      </div>
    );
  }

  const status = gathering.status ?? 'scheduled';

  if (status === 'live') {
    // GatheringRoom owns join + LiveKit. When the host ends the gathering,
    // its onDisconnected fires; the outer poll resumes and flips to recap
    // once the next tick observes status='ended'.
    return <GatheringRoom />;
  }

  if (status === 'ended') {
    return (
      <div className="min-h-dvh bg-background">
        <PostGathering />
      </div>
    );
  }

  return <ScheduledView gathering={gathering} meId={meId} onStarted={() => refetch()} />;
}
